import axios from 'axios';

// Схлопывает ПОДРЯД идущие точки с абсолютно одинаковыми координатами
// (lat/lon), оставляя из каждой такой группы последнюю по времени. Ожидает
// массив, уже отсортированный по возрастанию ts. Это убирает наложение
// маркеров и дублирование графиков, когда сенсор какое-то время стоял на месте,
// но сохраняет повторные визиты в ту же точку позже (они не идут подряд).
function collapseConsecutiveDuplicates(points) {
  if (!Array.isArray(points) || points.length < 2) return points || [];
  const out = [];
  for (const p of points) {
    const prev = out[out.length - 1];
    if (prev && prev.lat === p.lat && prev.lon === p.lon) {
      // Та же координата, что у предыдущей, — заменяем на более свежую точку.
      out[out.length - 1] = p;
    } else {
      out.push(p);
    }
  }
  return out;
}

const state = {
  startTime: null,
  endTime: null,
  avgTime: 0,
  isPoly: false,
  points: [],
  selectedPoint: null,
  loading: false,
  error: null,
  appliedAt: null,
  // Десятичная логарифмическая шкала (log₁₀) для графиков профайлинга.
  logScale: false,
};

const getters = {
  profilingPoints: s => s.points,
  profilingSelectedPoint: s => s.selectedPoint,
  profilingLoading: s => s.loading,
  profilingError: s => s.error,
  profilingLogScale: s => s.logScale,
  // Метка последнего полного (не инкрементального) Search — растёт при каждом
  // Apply/Search. Карта использует её, чтобы сбросить авто-следование.
  profilingAppliedAt: s => s.appliedAt,
  profilingFilters: s => ({
    startTime: s.startTime,
    endTime: s.endTime,
    avgTime: s.avgTime,
    isPoly: s.isPoly,
  }),
};

const mutations = {
  SET_PROFILING_FILTERS(state, { startTime, endTime, avgTime, isPoly }) {
    if (startTime !== undefined) state.startTime = startTime;
    if (endTime !== undefined) state.endTime = endTime;
    if (avgTime !== undefined) state.avgTime = avgTime;
    if (isPoly !== undefined) state.isPoly = isPoly;
  },
  SET_PROFILING_POINTS(state, points) {
    const deduped = collapseConsecutiveDuplicates(points || []);
    state.points = deduped;
    // Если выбранная точка была схлопнута как дубликат (её ts больше нет в
    // массиве), переводим выделение на выжившую точку с теми же координатами,
    // чтобы графики соответствовали видимому на карте маркеру.
    const sel = state.selectedPoint;
    if (sel && !deduped.some(p => p.ts === sel.ts)) {
      const survivor = deduped.find(p => p.lat === sel.lat && p.lon === sel.lon);
      if (survivor) state.selectedPoint = survivor;
    }
  },
  SET_PROFILING_LOADING(state, v) { state.loading = v; },
  SET_PROFILING_ERROR(state, e) { state.error = e; },
  SET_PROFILING_SELECTED(state, p) { state.selectedPoint = p; },
  SET_PROFILING_APPLIED_AT(state, ts) { state.appliedAt = ts; },
  SET_PROFILING_LOG_SCALE(state, v) { state.logScale = !!v; },
  CLEAR_PROFILING(state) {
    state.points = [];
    state.selectedPoint = null;
    state.error = null;
    state.appliedAt = null;
  },
};

const actions = {
  async searchProfilingPoints({ commit, state, rootState }, overrides = {}) {
    const filters = { ...{
      startTime: state.startTime,
      endTime: state.endTime,
      avgTime: state.avgTime,
      isPoly: state.isPoly,
    }, ...overrides };
    commit('SET_PROFILING_FILTERS', filters);
    commit('SET_PROFILING_LOADING', true);
    commit('SET_PROFILING_ERROR', null);
    try {
      const response = await axios.get('profiling/points', {
        params: {
          start_time: filters.startTime,
          end_time: filters.endTime,
          avg_time: filters.avgTime || 0,
          is_poly: !!filters.isPoly,
          // Тип лазера общий для всех вкладок (модуль results); пустое
          // значение фильтр не включает.
          ...(rootState.results && rootState.results.laser
            ? { laser: rootState.results.laser }
            : {}),
        },
      });
      commit('SET_PROFILING_POINTS', response.data.points || []);
      commit('SET_PROFILING_SELECTED', null);
      commit('SET_PROFILING_APPLIED_AT', Date.now());
    } catch (err) {
      console.error('Failed to fetch profiling points:', err);
      commit('SET_PROFILING_ERROR', err?.message || 'Request failed');
      commit('SET_PROFILING_POINTS', []);
    } finally {
      commit('SET_PROFILING_LOADING', false);
    }
  },
  // Инкрементальное обновление профайлинга в live-режиме. Точки сгруппированы
  // в бакеты по avg_time; последний бакет продолжает наполняться, поэтому его
  // нужно перезапросить целиком (а не только «новее последней точки»). Тянем
  // диапазон [ts последнего бакета .. now], заменяем trailing-бакет и добавляем
  // появившиеся новые бакеты.
  async refreshProfilingPointsLive({ commit, state, rootState }, { endTime } = {}) {
    const pts = state.points || [];
    const avg = state.avgTime || 0;
    const fetchStart = pts.length ? pts[pts.length - 1].ts : state.startTime;
    if (!fetchStart) return;
    const end = endTime || new Date().toISOString();
    try {
      const response = await axios.get('profiling/points', {
        params: {
          start_time: fetchStart,
          end_time: end,
          avg_time: avg,
          is_poly: !!state.isPoly,
          ...(rootState.results && rootState.results.laser
            ? { laser: rootState.results.laser }
            : {}),
        },
      });
      const incoming = response.data.points || [];
      commit('SET_PROFILING_FILTERS', { endTime: end });
      if (!incoming.length) return;
      // Отбрасываем локальные бакеты, попадающие в перезапрошенный хвост
      // (>= fetchStart), и заменяем их свежими из ответа.
      const cursorMs = new Date(fetchStart).getTime();
      const kept = pts.filter(p => new Date(p.ts).getTime() < cursorMs);
      const merged = kept
        .concat(incoming)
        .sort((a, b) => new Date(a.ts) - new Date(b.ts));
      commit('SET_PROFILING_POINTS', merged);
      // Если открыт график растущего (последнего) бакета — обновляем выбранную
      // точку тем же ts, чтобы профиль в панели пополнялся вживую.
      const sel = state.selectedPoint;
      if (sel && sel.ts) {
        const updated = incoming.find(p => p.ts === sel.ts);
        if (updated) commit('SET_PROFILING_SELECTED', { ...sel, ...updated });
      }
    } catch (err) {
      console.error('Failed live profiling refresh:', err);
    }
  },
  selectProfilingPoint({ commit }, point) {
    commit('SET_PROFILING_SELECTED', point);
  },
  closeProfilingDetails({ commit }) {
    commit('SET_PROFILING_SELECTED', null);
  },
  clearProfiling({ commit }) {
    commit('CLEAR_PROFILING');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
